import React, {
  createContext,
  useContext,
  useRef,
  useMemo,
  useCallback,
  useEffect,
  useState,
} from "react";
import { flushSync } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  setIsPlayingRound,
  updateTotalWin,
  setFreeSpinAutoplayActive,
  setSymbolsRevealed,
  playNextRound,
  setUserInterfaceDisabled,
  setCoverSymbolsInitial,
  setFreeSpinLinesRotationFinished,
  setFreeSpinWinCounterFinished,
  setIsFreeSpinDimActive,
} from "../State/actions";
import { useReelRefs } from "./ReelRefsContext";

/**
 * Controls free spin rounds, animations, and user interactions
 */
export const FreeSpinControlContext = createContext(null);

export const FreeSpinControlProvider = ({ children }) => {
  const dispatch = useDispatch();

  const {
    freeSpinReelRefs,
    freeSpinOverlayReelRefs,
    maskReelRefs,
    freeSpinWinLinesContainerRef,
  } = useReelRefs();

  const coveringRef = useRef(false);
  const autoplayTimeoutsRef = useRef([]);
  const autoplayProcessingRef = useRef(false);
  const [canSkipReveal, setCanSkipReveal] = useState(false);
  const skipRevealRef = useRef(false);
  const [isCovering, setIsCovering] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const revealedMidSymbolsRef = useRef(new Set());
  const suppressLandingOnSkipRef = useRef(new Set());
  const skipSuppressionActiveRef = useRef(false);
  const symbolsRevealedTimeRef = useRef(null);

  // DEBUG: Track processing counts to detect double processing
  const processingCounterRef = useRef(new Map());

  // Selectors
  const {
    symbolsRevealed,
    freeSpinsActive,
    currentFreeSpinRoundIndex,
    isPlayingRound,
    currentFreeSpinRound,
    freeSpinAutoplayActive,
  } = useSelector((state) => state.freeSpins);

  const {
    freeSpinLinesRotationFinished,
    freeSpinWinCounterFinished,
    showFreeSpinRetrigger,
  } = useSelector((state) => state.freeSpinAnimations);

  const { winAnimationsPlaying } = useSelector((state) => state.animations);

  // Track retrigger dialog state changes
  const prevShowRetriggerRef = useRef(showFreeSpinRetrigger);
  if (prevShowRetriggerRef.current !== showFreeSpinRetrigger) {
    prevShowRetriggerRef.current = showFreeSpinRetrigger;
  }

  /**
   * Clears all autoplay timeouts and animation frames
   */
  const clearAllAutoplayTimeouts = useCallback(() => {
    autoplayTimeoutsRef.current.forEach((id) => {
      clearTimeout(id);
      cancelAnimationFrame(id);
    });
    autoplayTimeoutsRef.current = [];
  }, []);

  /**
   * Helper to check if retrigger dialog will be triggered
   */
  const willTriggerRetrigger = useMemo(() => {
    return (
      freeSpinsActive &&
      currentFreeSpinRound?.type === "feature" &&
      freeSpinLinesRotationFinished &&
      symbolsRevealed
    );
  }, [
    freeSpinsActive,
    currentFreeSpinRound?.type,
    freeSpinLinesRotationFinished,
    symbolsRevealed,
  ]);

  /**
   * Cleans up visual effects - simplified to match main game approach
   */
  const immediateCleanup = useCallback(() => {
    // Clear winning symbol states (no more individual dimming)
    freeSpinReelRefs.current.forEach((reel) => {
      const controller = reel?.getFreeSpinReelController();
      if (controller) {
        controller.clearWinningSymbols();
        controller.removeVisualEffects();
        dispatch(setIsFreeSpinDimActive(true));
      }
    });

    // Hide all overlay animations
    freeSpinOverlayReelRefs.current.forEach((overlayReel) => {
      const controller = overlayReel?.getFreeSpinOverlayReelController();
      if (controller) {
        controller.hideAllAnimations();

        // Force deep cleanup to ensure spine state is clean
        if (controller.forceDeepSpineCleanup) {
          controller.forceDeepSpineCleanup();
        }
      }
    });

    // Hide win lines when covering symbols
    if (freeSpinWinLinesContainerRef.current) {
      freeSpinWinLinesContainerRef.current.hideWinLine();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Store the current reveal promise to prevent multiple simultaneous calls
  const revealPromiseRef = useRef(null);
  // Global skip signal that controllers can check
  const globalSkipSignalRef = useRef(false);

  /**
   * Reveals symbols with diagonal timing or instantly if skipped
   */
  const revealSymbols = useCallback(async () => {
    // If already revealing, speed up existing controllers and return the same promise
    if (revealPromiseRef.current) {
      // Speed up the existing controllers directly
      maskReelRefs.current.forEach((maskReel, reelIndex) => {
        if (maskReel && maskReel.speedUpReveal) {
          maskReel.speedUpReveal();
        }
      });

      return revealPromiseRef.current;
    }

    // Create and store the reveal promise
    const revealPromise = (async () => {
      const shouldSkip = skipRevealRef.current;
      // DON'T reset skipRevealRef here - let mask controllers read it first

      // If reveal is being skipped for this round and there is a win, pre-activate suppression
      if (shouldSkip) {
        try {
          const hasWin = currentFreeSpinRound?.win > 0;

          if (hasWin) {
            const refsCount = freeSpinReelRefs.current
              ? freeSpinReelRefs.current.length
              : 0;
            const slotfaceCount = Array.isArray(currentFreeSpinRound?.slotface)
              ? currentFreeSpinRound.slotface.length
              : 0;
            const totalReels = refsCount || slotfaceCount || 5;

            // Populate suppression for any symbols not already mid-triggered
            for (let reelIndex = 0; reelIndex < totalReels; reelIndex++) {
              for (let symbolIndex = 0; symbolIndex < 3; symbolIndex++) {
                const key = `${reelIndex}_${symbolIndex}`;
                if (!revealedMidSymbolsRef.current.has(key)) {
                  suppressLandingOnSkipRef.current.add(key);
                }
              }
            }
            skipSuppressionActiveRef.current =
              suppressLandingOnSkipRef.current.size > 0;
          }
        } catch (e) {}
      }

      // Turn off dimming when new round reveal starts
      dispatch(setIsFreeSpinDimActive(false));

      // Start all spine animations and collect their promises
      const revealPromises = maskReelRefs.current.map((maskReel, reelIndex) => {
        if (maskReel && maskReel.revealSymbols) {
          return maskReel.revealSymbols(shouldSkip);
        }
        return Promise.resolve(); // Fallback for missing reels
      });

      // Wait for ALL reels to complete their spine animations (100% completion)
      await Promise.all(revealPromises);

      // NOW reset the skip flag after mask controllers have processed it
      skipRevealRef.current = false;

      // Add minimum visual delay for all winning rounds to allow user to see the revealed symbols
      const hasWin = currentFreeSpinRound && currentFreeSpinRound.win > 0;
      if (hasWin) {
        // Give user minimum time to see the revealed symbols and dimming before win processing starts
        await new Promise((resolve) => setTimeout(resolve, 150));
      }

      // NOW all spines are 100% complete - dispatch the true reveal state
      dispatch(setSymbolsRevealed(true));
      symbolsRevealedTimeRef.current = Date.now();

      // Always disable button when symbols are fully revealed (changes to STOP icon but disabled)
      setIsButtonDisabled(true);

      // Update total win when symbols are truly revealed
      if (currentFreeSpinRound && currentFreeSpinRound.win > 0) {
        dispatch(updateTotalWin());
      }
    })();

    // Store the promise
    revealPromiseRef.current = revealPromise;

    // Clear the promise and reset skip signal when done
    revealPromise.finally(() => {
      revealPromiseRef.current = null;
      globalSkipSignalRef.current = false;
    });

    return revealPromise;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, currentFreeSpinRound]);

  /**
   * Covers symbols with spine animations
   * Uses COVER_RATIO (75%) to resolve when symbols are visually covered, avoiding empty animation space
   */
  const coverSymbols = useCallback(async () => {
    if (coveringRef.current) {
      return;
    }
    coveringRef.current = true;
    setIsCovering(true);

    // Clean up before covering to prevent overflow bugs
    immediateCleanup();

    // Start all cover animations and collect their promises
    const coverPromises = maskReelRefs.current.map((maskReel) => {
      if (maskReel && maskReel.coverSymbols) {
        return maskReel.coverSymbols(); // Returns Promise from COVER_RATIO (75% completion)
      }
      return Promise.resolve(); // Fallback for missing reels
    });

    // Wait for ALL reels to complete their cover animations (75% completion)
    await Promise.all(coverPromises);

    // CRITICAL: Force synchronous state updates to ensure button visual state changes immediately
    flushSync(() => {
      // Dispatch state changes synchronously to force immediate re-render
      dispatch(setSymbolsRevealed(false));
      dispatch(playNextRound());
      dispatch(setIsPlayingRound(false));
    });

    // Reset skip flag and suppression state for clean state in next round
    skipRevealRef.current = false;
    skipSuppressionActiveRef.current = false;
    suppressLandingOnSkipRef.current.clear();

    coveringRef.current = false;
    setIsCovering(false);

    // Enable button after visual state has updated (button now shows PLAY mode)
    setIsButtonDisabled(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [immediateCleanup, dispatch]);

  /**
   * Force-completes current round and advances immediately
   */
  const advanceRound = useCallback(() => {
    const hasWin = currentFreeSpinRound?.win > 0;
    const isLastRound = currentFreeSpinRound?.freeSpins === 0;
    const isFeatureRound = currentFreeSpinRound?.type === "feature";

    // Guard: require free spins active, symbols revealed, NOT the last round, and NOT a feature round
    // For winning rounds, require win counter to be finished
    if (
      !freeSpinsActive ||
      !symbolsRevealed ||
      isLastRound ||
      isFeatureRound ||
      showFreeSpinRetrigger ||
      (hasWin && !freeSpinWinCounterFinished) // Block manual advance for winning rounds until counter finishes
    ) {
      return;
    }

    if (coveringRef.current) {
      return;
    }

    // Cancel pending autoplay timers (user taking manual control)
    clearAllAutoplayTimeouts();
    autoplayProcessingRef.current = false;

    // For winning rounds: instantly mark animations as finished
    if (hasWin) {
      try {
        // Lines finished immediately
        dispatch(setFreeSpinLinesRotationFinished(true));
        // Win counter finished immediately (amount already tallied on reveal)
        dispatch(setFreeSpinWinCounterFinished(true));
      } catch (_) {}
    }

    // Clean overlays/visuals before covering (prevents overflow/races)
    immediateCleanup();

    // Trigger covering sequence to advance the round
    coverSymbols();
  }, [
    dispatch,
    freeSpinsActive,
    symbolsRevealed,
    currentFreeSpinRound,
    immediateCleanup,
    coverSymbols,
    showFreeSpinRetrigger,
    clearAllAutoplayTimeouts,
    freeSpinWinCounterFinished,
  ]);

  /**
   * Handle covering when retrigger dialog closes
   */
  const prevRetriggerStateRef = useRef(showFreeSpinRetrigger);
  useEffect(() => {
    const wasShowingRetrigger = prevRetriggerStateRef.current;
    const isNowHidden = !showFreeSpinRetrigger;

    if (
      wasShowingRetrigger &&
      isNowHidden &&
      symbolsRevealed &&
      freeSpinsActive
    ) {
      // Give user time to see the symbols after retrigger popup closes
      const timeoutId = setTimeout(() => {
        if (symbolsRevealed && freeSpinsActive) {
          coverSymbols();
        }
      }, 250);

      prevRetriggerStateRef.current = showFreeSpinRetrigger;

      return () => clearTimeout(timeoutId);
    }

    prevRetriggerStateRef.current = showFreeSpinRetrigger;
  }, [showFreeSpinRetrigger, symbolsRevealed, freeSpinsActive, coverSymbols]);

  /**
   * Starts a new round
   */
  const playRound = useCallback(async () => {
    // Guard against duplicate starts
    if (isPlayingRound || !freeSpinsActive || symbolsRevealed) {
      return;
    }

    // Clear autoplay processing flag when user manually starts a round
    autoplayProcessingRef.current = false;

    // CRITICAL: Clear suppression state FIRST to prevent any carryover from previous rounds
    skipSuppressionActiveRef.current = false;
    suppressLandingOnSkipRef.current.clear();

    // Reset state for new round
    revealedMidSymbolsRef.current.clear();
    processingCounterRef.current.clear();
    symbolsRevealedTimeRef.current = null;

    // Reset covering state for new round
    setIsCovering(false);

    // Reset animation flags to ensure clean state for each round
    if (freeSpinLinesRotationFinished || freeSpinWinCounterFinished) {
      dispatch(setFreeSpinLinesRotationFinished(false));
      dispatch(setFreeSpinWinCounterFinished(false));
    }

    dispatch(setIsPlayingRound(true));
    coveringRef.current = false;
    await revealSymbols();
  }, [
    isPlayingRound,
    freeSpinsActive,
    symbolsRevealed,
    dispatch,
    revealSymbols,
    freeSpinLinesRotationFinished,
    freeSpinWinCounterFinished,
  ]);

  /**
   * Skips reveal animation and instantly reveals all symbols
   */
  const skipReveal = useCallback(() => {
    if (!canSkipReveal) {
      return;
    }

    // Block skip during covering actions
    if (isCovering) {
      return;
    }

    clearAllAutoplayTimeouts();
    autoplayProcessingRef.current = false;
    skipRevealRef.current = true;

    // If this round has a win, enable suppression for symbols that haven't mid-triggered yet
    try {
      const hasWin = currentFreeSpinRound?.win > 0;

      suppressLandingOnSkipRef.current.clear();
      if (hasWin) {
        skipSuppressionActiveRef.current = true;
        const reelsCount = Array.isArray(currentFreeSpinRound?.slotface)
          ? currentFreeSpinRound.slotface.length
          : freeSpinReelRefs.current
          ? freeSpinReelRefs.current.length
          : 5;
        for (let reelIndex = 0; reelIndex < reelsCount; reelIndex++) {
          for (let symbolIndex = 0; symbolIndex < 3; symbolIndex++) {
            const key = `${reelIndex}_${symbolIndex}`;
            if (!revealedMidSymbolsRef.current.has(key)) {
              suppressLandingOnSkipRef.current.add(key);
            }
          }
        }
      } else {
        skipSuppressionActiveRef.current = false;
      }
    } catch (e) {
      skipSuppressionActiveRef.current = false;
      suppressLandingOnSkipRef.current.clear();
    }

    if (isPlayingRound) {
      // Skip current reveal animation
      revealSymbols();
    } else {
      // Start new round with skip
      skipRevealRef.current = true;
      playRound();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canSkipReveal,
    isCovering,
    isPlayingRound,
    playRound,
    clearAllAutoplayTimeouts,
    revealSymbols,
    currentFreeSpinRound,
  ]);

  const handleSymbolReveal = useCallback((reelIndex, symbolIndex, symbolId) => {
    const key = `${reelIndex}_${symbolIndex}`;

    const processCount = processingCounterRef.current.get(key) || 0;
    processingCounterRef.current.set(key, processCount + 1);

    if (processCount > 0) {
      // Double processing detected - this should not happen
    }

    if (!revealedMidSymbolsRef.current.has(key)) {
      revealedMidSymbolsRef.current.add(key);
    }

    const overlayReel = freeSpinOverlayReelRefs.current?.[reelIndex];

    const isSuppressed =
      skipSuppressionActiveRef.current &&
      suppressLandingOnSkipRef.current?.has(key);

    if (isSuppressed) {
      return;
    }
    if (overlayReel?.handleSymbolReveal) {
      overlayReel.handleSymbolReveal(symbolIndex, symbolId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Determine when skip reveal is available
   */
  useEffect(() => {
    const shouldAllowSkip =
      freeSpinsActive && currentFreeSpinRound && !symbolsRevealed;
    // Removed isProcessingAction check - allow skip during reveal process

    setCanSkipReveal(shouldAllowSkip);
  }, [symbolsRevealed, isPlayingRound, freeSpinsActive, currentFreeSpinRound]);

  /**
   * Reset button state when round state changes
   */
  useEffect(() => {
    if (!freeSpinsActive) {
      setIsButtonDisabled(true); // Disable button when free spins end
    } else {
      // Enable button when free spins start (if not already in a round)
      if (!isPlayingRound && !symbolsRevealed) {
        setIsButtonDisabled(false);
      }
    }
  }, [freeSpinsActive, isPlayingRound, symbolsRevealed]);

  /**
   * Reset processing flag when symbols are fully revealed
   * NOTE: This is now handled synchronously in coverSymbols() for better timing
   */
  // Removed redundant effect - isProcessingAction is reset synchronously in flushSync

  /**
   * Enable button for winning rounds when win counter finishes
   * Exclude retrigger rounds (feature rounds) and last rounds from being skippable
   */
  useEffect(() => {
    const hasWin = currentFreeSpinRound?.win > 0;
    const isFeatureRound = currentFreeSpinRound?.type === "feature";
    const isLastRound = currentFreeSpinRound?.freeSpins === 0;

    if (
      symbolsRevealed &&
      hasWin &&
      freeSpinWinCounterFinished &&
      isButtonDisabled &&
      !isCovering && // Don't re-enable if covering is in progress
      !isFeatureRound && // Don't enable for retrigger rounds (feature type)
      !isLastRound // Don't enable for last round (user must wait for outro)
    ) {
      // Enable button for winning rounds after counter finishes (except retriggers and last round)
      setIsButtonDisabled(false);
    }
  }, [
    symbolsRevealed,
    freeSpinWinCounterFinished,
    currentFreeSpinRound?.win,
    currentFreeSpinRound?.type,
    currentFreeSpinRound?.freeSpins,
    isButtonDisabled,
    isCovering,
  ]);

  /**
   * Re-enable button when round completes (for next round)
   * NOTE: This is now handled synchronously in coverSymbols() for better timing
   */
  // Removed redundant effect - isProcessingAction is reset synchronously in flushSync

  const contextValue = useMemo(() => {
    const isLastRound = currentFreeSpinRound?.freeSpins === 0;

    return {
      playRound,
      isPlayingRound,
      symbolsRevealed,
      hideMaskSpines: coverSymbols,
      immediateCleanup,
      handleSymbolReveal,
      canSkipReveal,
      skipReveal,
      advanceRound,
      isLastRound,
      isCovering,
      isButtonDisabled,
      setIsButtonDisabled,
      // ADD SUPPRESSION REFS (same as main game)
      suppressLandingOnSkipRef,
      skipSuppressionActiveRef,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    playRound,
    isPlayingRound,
    symbolsRevealed,
    coverSymbols,
    immediateCleanup,
    handleSymbolReveal,
    canSkipReveal,
    skipReveal,
    advanceRound,
    isCovering,
    isButtonDisabled,
    setIsButtonDisabled,
    currentFreeSpinRound?.freeSpins,
    // ADD SUPPRESSION REF DEPENDENCIES
    suppressLandingOnSkipRef,
    skipSuppressionActiveRef,
  ]);

  /**
   * Handle winning round progression
   * Manages the complete flow: wait for animations → reset round state → cover symbols
   */
  useEffect(() => {
    if (!freeSpinsActive) {
      return;
    }

    const isLastRound = currentFreeSpinRound?.freeSpins === 0;
    const hasWins = currentFreeSpinRound?.win && currentFreeSpinRound.win > 0;

    // Handle winning rounds: first reset isPlayingRound, then cover symbols
    if (
      hasWins &&
      symbolsRevealed &&
      freeSpinsActive &&
      !isLastRound &&
      !showFreeSpinRetrigger &&
      !willTriggerRetrigger
    ) {
      // Step 1: Reset isPlayingRound when animations are complete
      if (
        isPlayingRound &&
        symbolsRevealed &&
        freeSpinLinesRotationFinished &&
        freeSpinWinCounterFinished &&
        !winAnimationsPlaying
      ) {
        dispatch(setIsPlayingRound(false));
        return;
      }

      // Step 2: Cover symbols when round is complete and not manually controlled
      if (
        !isPlayingRound &&
        freeSpinLinesRotationFinished &&
        freeSpinWinCounterFinished &&
        !winAnimationsPlaying
      ) {
        coverSymbols();
        return;
      }
    }
  }, [
    freeSpinLinesRotationFinished,
    symbolsRevealed,
    freeSpinsActive,
    isPlayingRound,
    currentFreeSpinRoundIndex,
    coverSymbols,
    freeSpinWinCounterFinished,
    currentFreeSpinRound?.type,
    currentFreeSpinRound?.freeSpins,
    currentFreeSpinRound?.win,
    showFreeSpinRetrigger,
    winAnimationsPlaying,
    willTriggerRetrigger,
    dispatch,
  ]);

  /**
   * Handle final round cleanup before outro dialog
   */
  useEffect(() => {
    if (
      freeSpinsActive &&
      currentFreeSpinRound?.freeSpins === 0 &&
      symbolsRevealed
    ) {
      dispatch(setUserInterfaceDisabled(true));
      const hasWins = currentFreeSpinRound?.win && currentFreeSpinRound.win > 0;

      const performFinalCleanup = () => {
        immediateCleanup();
        dispatch(setCoverSymbolsInitial(true));
        // Clear suppression state at the end of sequence
        revealedMidSymbolsRef.current.clear();
        suppressLandingOnSkipRef.current.clear();
        skipSuppressionActiveRef.current = false;
      };

      if (
        hasWins &&
        freeSpinLinesRotationFinished &&
        freeSpinWinCounterFinished &&
        !showFreeSpinRetrigger &&
        !willTriggerRetrigger
      ) {
        performFinalCleanup();
      } else if (!hasWins && !showFreeSpinRetrigger && !willTriggerRetrigger) {
        // Give user extra time to see the final non-winning result before outro
        const timer = setTimeout(() => {
          performFinalCleanup();
        }, 1600);

        return () => clearTimeout(timer);
      }
    }
  }, [
    freeSpinsActive,
    currentFreeSpinRound?.freeSpins,
    currentFreeSpinRound?.win,
    currentFreeSpinRound?.type,
    dispatch,
    symbolsRevealed,
    freeSpinLinesRotationFinished,
    freeSpinWinCounterFinished,
    immediateCleanup,
    showFreeSpinRetrigger,
    willTriggerRetrigger,
  ]);

  /**
   * Main autoplay logic for automatic round progression
   */
  useEffect(() => {
    if (autoplayProcessingRef.current) {
      return;
    }

    if (
      !freeSpinsActive ||
      !freeSpinAutoplayActive ||
      showFreeSpinRetrigger ||
      winAnimationsPlaying
    ) {
      return;
    }

    if (!currentFreeSpinRound) {
      dispatch(setFreeSpinAutoplayActive(false));
      clearAllAutoplayTimeouts();
      return;
    }

    const isLastRound = currentFreeSpinRound?.freeSpins === 0;

    // Phase 1: Start revealing symbols
    if (!isPlayingRound && !symbolsRevealed) {
      autoplayProcessingRef.current = true;

      const timeoutId = setTimeout(() => {
        if (
          freeSpinAutoplayActive &&
          !isPlayingRound &&
          !symbolsRevealed &&
          freeSpinsActive
        ) {
          playRound();
        }
        autoplayProcessingRef.current = false;
      }, 100); // Small delay to allow everything to settle
      autoplayTimeoutsRef.current.push(timeoutId);

      return () => {
        clearTimeout(timeoutId);
        autoplayProcessingRef.current = false;
      };
    }

    // Reset isPlayingRound for non-winning rounds when symbols are revealed
    if (symbolsRevealed && isPlayingRound) {
      const hasWins = currentFreeSpinRound?.win && currentFreeSpinRound.win > 0;

      if (!hasWins) {
        dispatch(setIsPlayingRound(false));
      }

      return; // Winning rounds handled by separate effect
    }

    // Check if we can proceed with autoplay (don't wait for user interaction state)
    if (symbolsRevealed) {
      const currentRoundWin = currentFreeSpinRound?.win;
      const hasWins = currentRoundWin && currentRoundWin > 0;

      if (isLastRound) {
        dispatch(setFreeSpinAutoplayActive(false));
        clearAllAutoplayTimeouts();
        return;
      }

      if (!hasWins) {
        // Auto-cover non-winning rounds after 1600ms delay (autoplay only)
        const timeoutId = setTimeout(() => {
          if (
            freeSpinAutoplayActive &&
            symbolsRevealed &&
            freeSpinsActive &&
            !coveringRef.current
          ) {
            // Reset isPlayingRound here when we actually cover
            dispatch(setIsPlayingRound(false));
            coverSymbols();
          }
        }, 1600);
        autoplayTimeoutsRef.current.push(timeoutId);

        return () => clearTimeout(timeoutId);
      }
      // Winning rounds are handled by the win animation effect
    }
  }, [
    freeSpinAutoplayActive,
    freeSpinsActive,
    isPlayingRound,
    symbolsRevealed,
    currentFreeSpinRound,
    showFreeSpinRetrigger,
    winAnimationsPlaying,
    dispatch,
    playRound,
    coverSymbols,
    clearAllAutoplayTimeouts,
  ]);

  /**
   * Handle non-winning rounds in manual mode (1600ms delay)
   */
  useEffect(() => {
    const hasWins = currentFreeSpinRound?.win && currentFreeSpinRound.win > 0;
    const isLastRound = currentFreeSpinRound?.freeSpins === 0;

    // Only handle manual mode (when autoplay is not active)
    if (
      !freeSpinAutoplayActive &&
      symbolsRevealed &&
      !isPlayingRound &&
      !hasWins &&
      !isLastRound &&
      !coveringRef.current
    ) {
      const timeoutId = setTimeout(() => {
        if (symbolsRevealed && !coveringRef.current && !isPlayingRound) {
          coverSymbols();
        }
      }, 1600);

      autoplayTimeoutsRef.current.push(timeoutId);
      return () => clearTimeout(timeoutId);
    }
  }, [
    freeSpinAutoplayActive,
    symbolsRevealed,
    isPlayingRound,
    currentFreeSpinRound?.win,
    currentFreeSpinRound?.freeSpins,
    coverSymbols,
  ]);

  /**
   * Stop autoplay when free spins end
   */
  useEffect(() => {
    if (freeSpinAutoplayActive && !freeSpinsActive) {
      dispatch(setFreeSpinAutoplayActive(false));
      clearAllAutoplayTimeouts();
      // Clear suppression state when free spins end
      revealedMidSymbolsRef.current.clear();
      suppressLandingOnSkipRef.current.clear();
      skipSuppressionActiveRef.current = false;
      // Clean up suppression state when free spins end
    }
  }, [
    freeSpinAutoplayActive,
    freeSpinsActive,
    dispatch,
    clearAllAutoplayTimeouts,
  ]);

  return (
    <FreeSpinControlContext.Provider value={contextValue}>
      {children}
    </FreeSpinControlContext.Provider>
  );
};

export const useFreeSpinControl = () => {
  const context = useContext(FreeSpinControlContext);
  if (!context) {
    throw new Error(
      "useFreeSpinControl must be used within a FreeSpinControlProvider"
    );
  }
  return context;
};
